import { QrcodeOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, message, Popover, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceGetGuardianshipListEndpoint,
  usePersonsServiceGetPersonByIdEndpoint,
  usePersonsServiceGetPersonByIdEndpointKey,
  usePersonsServiceUpdatePersonEndpoint
} from 'src/api/queries'
import { ApiError, QrCodeType } from 'src/api/requests'
import { FormFooter, QRCode, Title } from 'src/components'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  clearedErrorsForm,
  formatDates,
  formErrorHandler,
  getLocalizedTitle,
  parseDates,
  removeCuatmStauts,
  toNullableObject,
  toDayjs
} from 'src/utils'

import { HistoryModal } from '../HistoryModal'
import { useStyles } from '../styles'

import { PersonalDetailsForm } from './components/PersonalDetailsForm'

export const PersonalDetails = () => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const { id } = useParams()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  const { data, isLoading } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { data: tutoreData, isLoading: isTutorLoading } =
    usePersonsServiceGetGuardianshipListEndpoint(
      { personId: String(id) },
      [String(id)],
      { enabled: !!id }
    )

  const { mutate, isPending } = usePersonsServiceUpdatePersonEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonByIdEndpointKey]
      })
      message.success(t('common.save_success'))

      const clearedErrors = clearedErrorsForm(form)
      form.setFields(clearedErrors)

      onToggle()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onToggle = () => setIsEditing((prev) => !prev)
  const onToggleHistory = () => setIsHistoryOpen((prev) => !prev)

  const onCancel = () => {
    onToggle()
    form.setFieldsValue({
      ...data,
      birthDate: toDayjs(data?.birthDate),
      demographics: parseDates(data?.demographics),
      currentAddress: {
        ...data?.currentAddress,
        cuatmLocalityId: data?.currentAddress?.cuatmLocality?.translation?.ro
      },
      residenceAddress: {
        ...data?.residenceAddress,
        cuatmLocalityId: data?.residenceAddress?.cuatmLocality?.translation?.ro
      }
    })
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    const isSameAddress = !!values.isResidenceAddressSameAsCurrent

    const valuesCuatmLocalityId = removeCuatmStauts(
      values?.currentAddress?.cuatmLocalityId
    )

    const valuesResidenceCuatmLocalityId = removeCuatmStauts(
      values?.residenceAddress?.cuatmLocalityId
    )

    const currentAddressCuatmLocalityId =
      valuesCuatmLocalityId ===
      data?.currentAddress?.cuatmLocality?.translation?.[currentLang.value]
        ? data?.currentAddress?.cuatmLocalityId
        : values?.currentAddress?.cuatmLocalityId

    const residenceAddressCuatmLocalityId =
      valuesResidenceCuatmLocalityId ===
      data?.residenceAddress?.cuatmLocality?.translation?.[currentLang.value]
        ? data?.residenceAddress?.cuatmLocalityId
        : values?.residenceAddress?.cuatmLocalityId

    const currentAddress = toNullableObject({
      ...values.currentAddress,
      cuatmLocalityId: currentAddressCuatmLocalityId
    })

    const residenceAddress = toNullableObject({
      country: isSameAddress
        ? values?.currentAddress?.country
        : values?.residenceAddress?.country,
      cuatmLocalityId: isSameAddress
        ? currentAddressCuatmLocalityId
        : values?.residenceAddress?.country === 'md'
          ? residenceAddressCuatmLocalityId
          : null,
      street: isSameAddress
        ? values?.currentAddress?.street
        : values?.residenceAddress?.street,
      streetNumber: isSameAddress
        ? values?.currentAddress?.streetNumber
        : values?.residenceAddress?.streetNumber,
      apartmentNumber: isSameAddress
        ? values?.currentAddress?.apartmentNumber
        : values?.residenceAddress?.apartmentNumber
    })

    const requestBody = {
      ...values,
      penitentiaryRecords: data?.penitentiaryRecords,
      isAnonymous: values?.isAnonymousChecked,
      currentAddress,
      residenceAddress,
      birthDate: toDayjs(values.birthDate)?.format(yearMonthDaySlash),
      // Sent as a whole: the API replaces the stored demographics with it.
      demographics: formatDates(values.demographics)
    }

    mutate({ requestBody, id: String(id) })
  }

  useEffect(() => {
    if (data && tutoreData) {
      form.setFieldsValue({
        ...data,
        birthDate: toDayjs(data.birthDate),
        demographics: parseDates(data.demographics),
        currentAddress: {
          ...data.currentAddress,
          country: data?.currentAddress?.country || 'md',
          cuatmLocalityId: getLocalizedTitle({
            item: data?.currentAddress?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        },
        residenceAddress: {
          ...data?.residenceAddress,
          country: data?.residenceAddress?.country || 'md',
          cuatmLocalityId: getLocalizedTitle({
            item: data?.residenceAddress?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        },
        socialStatusId: data?.socialStatus?.id,
        tutor: tutoreData?.status,
        isAnonymous: data.isAnonymous,
        isAnonymousChecked: data.isAnonymous,
        testrapCode: data?.testrapCode,
        citizenship: data?.citizenship || 'md',
        hasTutorship: (tutoreData?.guardianships?.length ?? 0) > 0,
        hasPrison: !!data?.penitentiaryRecords?.length
      })
    }
  }, [data, tutoreData])

  return (
    <Spin spinning={isLoading || isTutorLoading}>
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="flex-end">
          <Title className={styles.title}>{t('users.general_info')}</Title>

          <Flex gap={8} align="center">
            <Button onClick={onToggleHistory}>
              {t('persons.show_history')}
            </Button>

            <Popover
              placement="bottom"
              trigger={['click']}
              content={<QRCode type={QrCodeType.PERSON} content={id} />}
            >
              <Button icon={<QrcodeOutlined />}>
                {t('qr_code.view_qr_code')}
              </Button>
            </Popover>

            {!isEditing && (
              <Button type="primary" onClick={onToggle}>
                {t('common.edit')}
              </Button>
            )}
          </Flex>
        </Flex>

        <PersonalDetailsForm
          form={form}
          isEditing={isEditing}
          showTutor
          showDemographics
        />

        <FormFooter
          hidden={!isEditing}
          onCancel={onCancel}
          onSave={onSubmit}
          saveConfirmTitle={`${t('persons.save')}?`}
          saveLabel={t('persons.save')}
          loading={isPending}
        />
      </Flex>

      {isHistoryOpen && <HistoryModal onCancel={onToggleHistory} />}
    </Spin>
  )
}
